import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, MapPin, Calendar as CalendarIcon, Award, Languages, ArrowLeft, GraduationCap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LoadingState } from "@/components/ui/loading-state";
import { getLanguageText, getExperienceText, getSpecialtyApproachesText } from "@/lib/therapist-text-helpers";

const specializationLabels: Record<string, string> = {
  career: "Career Counselling",
  anxiety: "Anxiety & Stress",
  depression: "Depression",
  relationship: "Relationship Counselling",
  trauma: "Trauma & PTSD",
  addiction: "Addiction Recovery",
  family: "Family Therapy",
  grief: "Grief & Loss",
  stress: "Stress Management",
};

const getSpecialtyLabel = (specialty: string): string =>
  specializationLabels[specialty.toLowerCase()] || specialty;

// Utility function to check if an array has meaningful content
const hasContent = (arr: any[] | null | undefined): boolean => {
  if (!arr || arr.length === 0) {
    return false;
  }
  // Check if at least one item in the array has a non-empty property (e.g., degree, institution)
  return arr.some((item) => Object.values(item).some((value) => value !== null && value !== undefined && value !== ""));
};

export default function TherapistProfile() {
  const params = useParams();
  const therapistIdOrSlug = params.slug || params.id || "1";
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Fetch therapist data from API and handle redirect
  const [therapist, setTherapist] = useState<{
    id: number;
    slug: string;
    name: string;
    email: string;
    title: string;
    specialty: string;
    avatar?: string;
    bio?: string;
    rating?: number;
    reviews?: number;
    location?: string;
    experience?: string;
    priceRange?: string;
    verified?: boolean;
    languages: string[];
    approaches: string[];
    sessionTypes: Array<{ type: string; price: number; duration: number }>; // Explicitly define sessionTypes structure
    insurance: string[];
    education: Array<{ degree: string; institution: string; year: string }>;
    certifications: string[];
    availability: Record<string, any>;
    pricing?: {
      individual?: { price: number; duration?: number };
      couples?: { price: number; duration?: number };
      family?: { price: number; duration?: number };
      group?: { price: number; duration?: number };
    };
    freeConsultation?: boolean; // Added for free consultation logic
    licenseNumber?: string; // Added for license number
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTherapist = async () => {
      // Handle invalid therapist ID/slug
      if (!therapistIdOrSlug || therapistIdOrSlug === "undefined" || therapistIdOrSlug === "null") {
        toast({
          title: "Invalid Therapist",
          description: "The therapist profile link is invalid.",
          variant: "destructive",
        });
        navigate('/therapists');
        return;
      }

      // Check if we're fetching by numeric ID - if so, skip API call and navigate directly
      const isNumericId = /^\d+$/.test(therapistIdOrSlug);

      try {
        setLoading(true);
        console.log('Fetching therapist with identifier:', therapistIdOrSlug);

        // Add cache-busting to force fresh data
        const response = await fetch(`/api/therapists/${therapistIdOrSlug}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        const data = await response.json();

        // Check if response indicates a redirect
        if (data.redirect) {
          // Check if it's a slug enforcement redirect (permanent redirect for SEO)
          if (data.permanent && data.redirectTo && data.redirectTo.startsWith('/therapist-profile/')) {
            // Extract the slug from the redirect URL
            const slug = data.redirectTo.replace('/therapist-profile/', '');

            console.log('Received redirect to slug:', slug, 'Current identifier:', therapistIdOrSlug);

            // Navigate to slug URL without fetching again
            setLoading(false);
            navigate(`/therapist-profile/${slug}`, { replace: true });
            return; // Stop here - the navigation will trigger a new useEffect with the slug
          }

          // Other redirects (inactive therapist, not found, etc.)
          setLoading(false);
          toast({
            title: "Therapist Not Available",
            description: data.message,
            variant: "destructive",
          });
          navigate(data.redirectTo || '/therapists');
          return;
        }

        // Parse JSON fields and set therapist data
        console.log('Setting therapist data for:', therapistIdOrSlug);
        setTherapist({
          ...data,
          sessionTypes: data.sessionTypes || [],
          availability: data.availability || {},
          languages: data.languages || [],
          approaches: data.approaches || [],
          insurance: data.insurance || [],
          education: data.education || [],
          certifications: data.certifications || [],
          freeConsultation: data.freeConsultation || false, // Set default value
          licenseNumber: data.licenseNumber || '' // Set default value
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch therapist:', error);
        setLoading(false);
        toast({
          title: "Error",
          description: "Failed to load therapist profile",
          variant: "destructive",
        });
        navigate('/therapists');
      }
    };

    fetchTherapist();
  }, [therapistIdOrSlug, navigate, toast]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState context="profile" />
      </div>
    );
  }

  if (!therapist) {
    return null;
  }

  // Use therapist data - in real app would fetch from API
  const therapistData = {
    ...therapist
  };

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="mx-auto max-w-6xl">
      {/* Back Button */}
      <div className="mb-6">
        <Link href="/therapists">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Human Mitr
          </Button>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        {/* Main Profile Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex gap-6">
                <div className="relative">
                  <Avatar className="w-32 h-32">
                    {therapistData.avatar && <AvatarImage 
                      src={therapistData.avatar} 
                      alt={therapistData.name || 'Therapist'}
                    />}
                    <AvatarFallback className="text-3xl font-semibold bg-primary/10 text-primary">
                      {(therapistData.name || 'T').split(' ').map((n: string) => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  {therapistData.verified && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-4">
                    <div className="min-w-0">
                      <h1 className="text-2xl font-bold text-gray-900">{therapist.name}</h1>
                      <p className="text-gray-600">{therapist.title}</p>
                      <Badge variant="secondary" className="mt-2 whitespace-nowrap">{getSpecialtyLabel(therapist.specialty)}</Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {therapist.rating && (
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">{therapist.rating}</span>
                        <span className="text-gray-500">({therapist.reviews} reviews)</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{therapist.location}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        // Navigate to booking page with therapist pre-selected
                        navigate(`/booking?therapist=${therapist.id}&session=human`);
                      }}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Book Session
                    </Button>
                    
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* About Section */}
          <Card>
            <CardHeader>
              <CardTitle>About {therapist.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {therapist.bio && (
                <div className="text-gray-700 whitespace-pre-line">
                  {therapist.bio}
                </div>
              )}

              {/* Humanized Expertise & Approach */}
              {(therapist.specialty || (therapist.approaches && therapist.approaches.length > 0)) && (
                <p className="text-gray-700 leading-relaxed">
                  {getSpecialtyApproachesText({
                    id: therapist.id,
                    name: therapist.name,
                    specialty: getSpecialtyLabel(therapist.specialty),
                    approaches: therapist.approaches
                  })}
                </p>
              )}

              {/* Humanized Experience */}
              {therapist.experience && (
                <p className="text-gray-700 leading-relaxed">
                  {getExperienceText({
                    id: therapist.id,
                    name: therapist.name,
                    experience: therapist.experience
                  })}
                </p>
              )}

              {/* Humanized Languages */}
              {therapist.languages && therapist.languages.length > 0 && (
                <p className="text-gray-600 leading-relaxed italic">
                  {getLanguageText({
                    id: therapist.id,
                    name: therapist.name,
                    languages: therapist.languages
                  })}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Reviews - Hidden: Mock data not from DB */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Session Types & Pricing */}
          {therapist.sessionTypes && therapist.sessionTypes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Session Types & Pricing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Render session types from sessionTypes array */}
                {therapist.sessionTypes.map((session: any, index: number) => {
                  const sessionData = typeof session === 'string' 
                    ? { type: session, price: 0, duration: 50 }
                    : session;

                  const duration = sessionData.duration || 50;
                  const price = sessionData.price || 0;

                  // Determine display price
                  let displayPrice = '';

                  // Case 1: Free consultation enabled AND price is 0
                  if (therapist.freeConsultation && price === 0) {
                    displayPrice = 'Free Consultation';
                  }
                  // Case 2: Price from sessionTypes
                  else if (price > 0) {
                    displayPrice = `₹${price.toLocaleString('en-IN')}`;
                  }
                  // Case 3: Fallback to pricing object
                  else if (therapist.pricing) {
                    const pricingKey = sessionData.type.toLowerCase().replace(' therapy', '').replace(' ', '');
                    const pricingInfo = therapist.pricing[pricingKey as keyof typeof therapist.pricing];
                    if (pricingInfo && typeof pricingInfo === 'object' && pricingInfo.price > 0) {
                      displayPrice = `₹${pricingInfo.price.toLocaleString('en-IN')}`;
                    } else {
                      displayPrice = 'Price on request';
                    }
                  }
                  // Case 4: No valid pricing found
                  else {
                    displayPrice = 'Price on request';
                  }

                  return (
                    <div key={`${sessionData.type}-${index}`} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{sessionData.type}</p>
                        <p className="text-sm text-gray-600">{duration} mins</p>
                      </div>
                      <span className="font-semibold text-sage-700">
                        {displayPrice}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Education */}
          {hasContent(therapist.education) && (
            <Card>
              <CardHeader>
                <CardTitle>Education</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {therapist.education?.map((edu: any, index: number) => (
                  <div key={`edu-${index}`} className="flex items-start space-x-2">
                    <GraduationCap className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">{edu.degree}</p>
                      <p className="text-sm text-gray-600">{edu.institution}</p>
                      <p className="text-xs text-gray-500">{edu.year}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Certifications */}
          {therapist.certifications && therapist.certifications.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Certifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {therapist.certifications?.map((cert: string, index: number) => (
                    <Badge key={`cert-${index}`} variant="secondary">
                      <Award className="h-3 w-3 mr-1" />
                      {cert}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Insurance */}
          {therapist.insurance && therapist.insurance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Accepted Insurance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {therapist.insurance.map((insurance, index) => (
                  <p key={index} className="text-sm">{insurance}</p>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      </div>

    </div>
  );
}